import { memo } from 'react'
import { useContractChangeStoragePage } from './useContractChangeStoragePage'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { ValueChangedEvent as NumberBoxValueChangedEvent } from 'devextreme/ui/number_box'
import { BillingCycle } from '@crew/enums/app'
import { useTranslation } from '@crew/modules/i18n'
import { useCrewNavigate } from 'hooks/useCrewNavigate'
import { useCallback, useMemo } from 'react'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useToast } from 'hooks/useToast'
import {
  useGetBillingCycleQuery,
  useGetStorageCapacityQuery,
} from '@crew/apis/contract/contractApis'
import { FormValues } from './useContractChangeStoragePage'
import { formatContractStorageSize } from '@crew/utils/number'
import { useWatch } from 'react-hook-form'
import { CrewNumberBoxField } from 'components/forms/crewNumberBoxField'
import { GetStorageCapacityResponse_StorageCapacityTiers } from '@crew/apis/contract/models/getStorageCapacity/response'
import { MINIMUM_STORAGE_CAPACITY } from '@crew/configs/constants'

/**
 *  Contract change storage page
 * @date 8/16/2023 -9:14:14 AM
 *
 * @type {*}
 */

// TODO: 英語表記にした際に崩れるので要対応
// https://break-tmc.atlassian.net/browse/CREW-14306
export const ContractChangeStoragePage = memo(() => {
  const {
    control,
    handleSubmit,
    setValue,
    updateStorageCapacity,
    isLoadingUpdateStorageCapacity,
  } = useContractChangeStoragePage()

  const { historyBack, navigate } = useCrewNavigate()

  const { t } = useTranslation()

  const [showApiErrors] = useShowApiErrors()

  const { success } = useToast()

  const { data: storageData, isFetching } = useGetStorageCapacityQuery()

  // handle upgrade contact plan
  const handleChangeStoragePlanButtonClick = useCallback(() => {
    // react-hook-formのhandleSubmitに渡すコールバック関数を定義する
    const onSubmit = async (data: FormValues) => {
      try {
        // Execute update storage capacity process
        await updateStorageCapacity(data.storageUpgrade)

        success(t('message.contract.changeStoragePlanSuccess'))

        // go contract portal page
        navigate('/contract/portal')
      } catch (error) {
        showApiErrors(error)
      }
    }

    // ファイル更新実行
    handleSubmit(onSubmit)()
  }, [handleSubmit, updateStorageCapacity, success, t, navigate, showApiErrors])

  // go back prev screen
  const handleCancelButtonClick = useCallback(() => {
    historyBack()
  }, [historyBack])

  // Get the  list of billing cycle
  const { data: billingCycleData } = useGetBillingCycleQuery()

  // 契約中の請求サイクル
  const currentBillingCycle = useMemo(() => {
    const currentBillingCycle = billingCycleData?.billingCycle.find((item) => {
      return item.isCurrentCycle === true
    })?.billingCycle

    // 型制約上undefined許容になるため型チェックを行う
    if (!currentBillingCycle) return undefined

    return currentBillingCycle
  }, [billingCycleData?.billingCycle])

  // 契約中のストレージ容量の金額
  const currentStorageAmount = useMemo(() => {
    // 契約中の情報が取得できない場合は「-」
    if (!storageData || isFetching) {
      return '-'
    }

    // 金額が0の場合は「無料」
    if (storageData.currentStorageCapacity.amount === 0) {
      return t('label.free')
    }

    const price = storageData.currentStorageCapacity.amount.toLocaleString()

    // {金額}{請求単位}の形式で金額表示
    return currentBillingCycle === BillingCycle.Month
      ? t('label.priceMonthlyText', {
          price: price,
        })
      : t('label.priceAnnualText', {
          price: price,
        })
  }, [currentBillingCycle, isFetching, storageData, t])

  // 選択中のストレージ容量
  const storageUpgrade = useWatch({
    control,
    name: 'storageUpgrade',
  })

  // ストレージ容量の金額を計算する関数
  const calculateStorageCapacityAmount = useCallback(
    (
      capacity: number,
      tiers: GetStorageCapacityResponse_StorageCapacityTiers[]
    ) => {
      let amount = 0

      for (const tier of tiers) {
        const unitAmount = tier.unitAmountDecimal

        if (tier.upTo !== null && tier.upTo > 0) {
          // 段階的ティアが続く場合
          let calculateCapacity: number
          if (capacity <= tier.upTo) {
            calculateCapacity = capacity
          } else {
            calculateCapacity = tier.upTo
          }
          amount += calculateCapacity * unitAmount
          capacity -= calculateCapacity
        } else {
          // 最後のティア（up_to === null または 0）
          amount += capacity * unitAmount
          capacity = 0
        }

        if (capacity === 0) {
          break
        }
      }

      return amount
    },
    []
  )

  // 変更後のストレージ容量の金額を計算して表示する
  const upgradedStorageCapacityAmount = useMemo(() => {
    // 変更後の情報が取得できない場合は「-」
    if (!storageData || isFetching) {
      return ''
    }

    const calcAmount = calculateStorageCapacityAmount(
      storageUpgrade,
      storageData.storageCapacityTiers
    )

    // 金額が0の場合は「無料」
    if (calcAmount === 0) {
      return t('label.free')
    }

    const price = calcAmount.toLocaleString()

    // {金額}{請求単位}の形式で金額表示
    return currentBillingCycle === BillingCycle.Month
      ? t('label.priceMonthlyText', {
          price: price,
        })
      : t('label.priceAnnualText', {
          price: price,
        })
  }, [
    calculateStorageCapacityAmount,
    currentBillingCycle,
    isFetching,
    storageData,
    storageUpgrade,
    t,
  ])

  // ナンバーボックスの値変更時の処理、10単位での入力にする
  const handleNumberOfStorageUpgradeValueChanged = useCallback(
    (e: NumberBoxValueChangedEvent) => {
      if (e.value !== undefined) {
        // 10単位での入力にする
        const newValue = Math.floor(e.value / 10) * 10
        setValue('storageUpgrade', newValue)
      }
    },
    [setValue]
  )

  return (
    <form>
      <div className="flex flex-col gap-y-2.5 mx-auto w-full max-w-2xl">
        <div className="py-2.5 w-full flex-col justify-start items-start gap-2.5 flex">
          <span className="text-md font-bold">
            {t('label.changeStorage.title')}
          </span>
          <div className="flex flex-col gap-1.5 text-md">
            <div>{t('label.changeStorage.description')}</div>
            <div>{t('label.changeStorage.note')}</div>
          </div>

          <div className="flex-col w-full justify-start items-start gap-2.5 inline-flex">
            {/* 現在のストレージ容量 */}
            <div className="w-full justify-start items-center gap-2.5 inline-flex">
              {/* 「現在」のラベル */}
              <div className="w-20 self-stretch justify-start items-center gap-2.5 flex">
                <div className="crew-text-gray-4 text-md ">
                  {t('label.contractNow')}
                </div>
              </div>

              {/* 組織全体でxxまで使用可能 */}
              <div className="grow shrink justify-start items-center gap-2.5 flex">
                <div className="crew-text-gray-4 text-md ">
                  {t('label.acrossTheOrganization')}
                </div>
                <div className="crew-text-gray-4 text-md ">
                  {/** format storage display */}
                  {!isFetching && storageData?.currentStorageCapacity
                    ? formatContractStorageSize(
                        storageData.currentStorageCapacity.storageCapacity
                      )
                    : '-'}
                </div>
                <div className="crew-text-gray-4 text-md ">
                  {t('label.canBeUse')}
                </div>
              </div>

              {/* 金額 */}
              <div className="self-stretch justify-end items-center gap-2.5 flex">
                <div className=" text-blue-500 text-md ">
                  {currentStorageAmount}
                </div>
              </div>
            </div>

            {/* 変更後のストレージ容量 */}
            <div className="self-stretch justify-start items-center gap-2.5 inline-flex">
              {/* 「変更後」のラベル */}
              <div className="w-20 self-stretch justify-start items-center gap-2.5 flex">
                <div className="crew-text-gray-4 text-md ">
                  {t('label.contractAfterChange')}
                </div>
              </div>

              {/* 組織全体でxxまで使用可能 */}
              <div className="grow shrink">
                <div className="justify-start items-center gap-2.5 flex">
                  <div className="crew-text-gray-4 text-md justify-center">
                    {t('label.acrossTheOrganization')}
                  </div>
                  <CrewNumberBoxField
                    control={control}
                    id="storageUpgrade"
                    name="storageUpgrade"
                    step={10}
                    showLabel={false}
                    width={100}
                    className="mx-1"
                    onValueChanged={handleNumberOfStorageUpgradeValueChanged}
                    min={MINIMUM_STORAGE_CAPACITY}
                    showSpinButtons={true}
                  />

                  <div className="crew-text-gray-4 text-md ">
                    {t('label.canBeUseWithUnit')}
                  </div>
                </div>
                <div className="text-red-500 text-sm leading-5">
                  {t('label.canBeChangeIncremets')}
                </div>
              </div>

              {/* 金額 */}
              <div className="self-stretch justify-end items-center gap-2.5 flex">
                <div className=" text-blue-500 text-md ">
                  {upgradedStorageCapacityAmount}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-row gap-x-2.5 items-center">
          {/* Change storage button */}
          <CrewButton
            text={t('action.change')}
            type="primary"
            onClick={handleChangeStoragePlanButtonClick}
            disabled={isFetching || isLoadingUpdateStorageCapacity}
          />

          {/* Cancel button */}
          <CrewButton
            text={t('action.cancel')}
            type="normal"
            stylingMode="outlined"
            onClick={handleCancelButtonClick}
          />
        </div>
      </div>
    </form>
  )
})
